import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";

import {
  addRule, byLens, defaultDraft, draftOf, metricNoData, operatorOptions, ruleBody, rulePriority, ruleSentence, saveTemplate, scopeOptions, setRule,
  STRATEGIES_COPY as T, type ChannelScope, type Library, type Operator, type Rule, type RuleAnswer, type RuleBody, type RuleDraft, type StrategyEntry, type Template, type TemplateAnswer,
} from "./strategies";
import { FormError, MetricSelect, NoData, unitMark, useStrategyWrite, type Editor } from "./StrategyParts";

/** What the builder is for (v26 `builderHtml`): a rule template for the library (from the library, or from a card, which then also
 * adds it to that strategy), or a rule appended to or replacing one of a strategy. */
export type BuilderMode =
  | { kind: "template"; strategy: StrategyEntry | null }
  | { kind: "add"; strategy: StrategyEntry }
  | { kind: "edit"; strategy: StrategyEntry; rule: Rule };

/** The builder still open over a card's rules: an edit builder holds its rule's id, and a reorder, a rule delete or an edit made
 * elsewhere gives the rule a new one (C2 answers 409 `changed_elsewhere` to the old). Once the card's rules no longer carry that
 * id the builder closes, so Edit is reopened on the current row rather than retried with an id that can never save. */
export function liveBuilder(mode: BuilderMode | null, rules: Rule[]): BuilderMode | null {
  return mode?.kind === "edit" && !rules.some((r) => r.id === mode.rule.id) ? null : mode;
}

const blank: RuleDraft = { metric: "", operator: ">=", threshold: "", channel_scope: "any" };
const sameTuple = (t: Template, d: RuleDraft) => t.metric === d.metric && t.operator === d.operator && t.threshold === d.threshold && t.channel_scope === d.channel_scope;

/**
 * The inline rule builder under the library or a strategy card. A rule is picked from the library (which fills the four fields) or
 * typed: metric (grouped by lens), operator (at least, at most, exactly), threshold, condition. The browser sends the tuple as typed;
 * C2 appends at the end, replaces by rule id, and finds or creates the matching library template on every rule write (design
 * calls A and D). A rule made from a card's "Create new rule" is one append, so the rule and its template land together.
 */
export function RuleBuilder({ mode, library, editor, onClose, onCreateNew }: { mode: BuilderMode; library: Library; editor: Editor; onClose: () => void; onCreateNew?: () => void }) {
  const [draft, setDraft] = useState<RuleDraft>(mode.kind === "edit" ? draftOf(mode.rule) : mode.kind === "add" ? defaultDraft(library) : blank);
  const [error, setError] = useState<string | null>(null);
  const strategy = mode.strategy;
  const save = useStrategyWrite<RuleBody, TemplateAnswer | RuleAnswer>(editor, (body) => {
    if (mode.kind === "template" && !strategy) return saveTemplate(body);
    if (mode.kind === "edit") return setRule(mode.strategy.id, mode.rule.id, body);
    return addRule((strategy as StrategyEntry).id, body);
  }, (answer) => {
    if ("unchanged" in answer && answer.unchanged) return T.done.unchanged;
    if (!("rule_id" in answer)) return T.done.templateSaved(answer.text);
    const at = rulePriority(answer, answer.rule_id) ?? answer.rules.length;
    const rule = answer.rules.find((r) => r.id === answer.rule_id);
    if (mode.kind === "template") return T.done.templateSaved(rule ? ruleSentence(rule, library.metrics) : "", answer.name);
    return mode.kind === "edit" ? T.done.ruleSaved(at, answer.name) : T.done.ruleAdded(at, answer.name);
  });
  const submit = () => {
    const body = ruleBody(draft);
    if (!body.ok) { setError(body.error); return; }
    setError(null);
    save.mutate(body.body, { onSuccess: onClose });
  };
  const set = (patch: Partial<RuleDraft>) => setDraft({ ...draft, ...patch });
  const title = mode.kind === "template"
    ? (strategy ? T.builder.templateTitleFor(strategy.name) : T.builder.templateTitle)
    : mode.kind === "edit" ? T.builder.editTitle(mode.rule.priority, mode.strategy.name) : T.builder.addTitle(mode.strategy.name);
  const templates = library.templates ?? [];
  const picked = templates.find((t) => sameTuple(t, draft));
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-ks-grey-4 p-3" data-builder={mode.kind}>
      <div className="font-semibold text-ks-ink">{title}</div>
      {mode.kind !== "template" && templates.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-ks-grey-1">{T.builder.fromLibrary}</span>
          <Select value={picked ? String(picked.id) : ""} onValueChange={(v) => { const t = templates.find((x) => String(x.id) === v); if (t) setDraft(draftOf(t)); }}>
            <SelectTrigger className="w-[28rem] max-w-full *:data-[slot=select-value]:min-w-0 *:data-[slot=select-value]:truncate" aria-label={T.builder.fromLibrary}><SelectValue placeholder={T.builder.pickTemplate}>{picked?.text}</SelectValue></SelectTrigger>
            <SelectContent>
              {byLens(templates).map((g) => (
                <SelectGroup key={g.lens ?? "none"}>
                  {g.label && <SelectLabel>{g.label}</SelectLabel>}
                  {g.items.map((t) => <SelectItem key={t.id} value={String(t.id)}>{t.text}</SelectItem>)}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-ks-grey-1">{T.builder.metric}</span>
        <MetricSelect metrics={library.metrics} value={draft.metric} onChange={(metric) => set({ metric })} label={T.builder.metric} placeholder={T.builder.pickMetric} />
        <span className="text-xs text-ks-grey-1">{T.builder.operator}</span>
        <Select value={draft.operator} onValueChange={(v) => set({ operator: v as Operator })}>
          <SelectTrigger className="w-32" aria-label={T.builder.operator}><SelectValue>{T.operator[draft.operator]}</SelectValue></SelectTrigger>
          <SelectContent>{operatorOptions(draft.operator).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-xs text-ks-grey-1">{T.builder.threshold}</span>
        <Input className="w-20" inputMode="decimal" aria-label={T.builder.threshold} value={draft.threshold} onChange={(e) => set({ threshold: e.target.value })} />
        <span className="text-sm text-ks-grey-1">{unitMark(library.metrics, draft.metric)}</span>
        <Select value={draft.channel_scope} onValueChange={(v) => set({ channel_scope: v as ChannelScope })}>
          <SelectTrigger className="w-44" aria-label={T.builder.condition}><SelectValue>{T.scope[draft.channel_scope]}</SelectValue></SelectTrigger>
          <SelectContent>{scopeOptions().map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
        <NoData reason={metricNoData(library.metrics, draft.metric)} />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={submit} disabled={save.isPending}>{mode.kind === "template" ? T.builder.saveToLibrary : T.builder.saveRule}</Button>
        <Button size="sm" variant="outline" data-nonmutating onClick={onClose}>{T.builder.cancel}</Button>
        {mode.kind !== "template" && onCreateNew && <Button size="sm" variant="link" onClick={onCreateNew}>{T.builder.createNew}</Button>}
      </div>
      <FormError error={error} />
    </div>
  );
}
